import React, { useState, useEffect } from 'react';
import { Plus, Shield, AlertTriangle, Siren, CheckCircle2, Baby } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { security as api, type SecurityLog, type ApiError } from '../../services/api';
import DataGrid from '../../components/DataGrid';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

const INIT = { eventType: 'gate-entry', description: '', severity: 'info', location: '' };

const EVENT_TYPES = [
  { value: 'gate-entry', label: 'Gate Entry' },
  { value: 'panic', label: 'Panic Alert' },
  { value: 'child-exit', label: 'Child Safety Alert' },
  { value: 'patrol', label: 'Patrol Log' },
  { value: 'incident', label: 'Incident' },
];

const norm = (s: string | undefined) => (s ?? '').toLowerCase().replace(/_/g, '-');

const severityColor = (raw: string | undefined) => {
  const s = norm(raw);
  return s === 'critical' ? { bg: 'rgba(239,68,68,0.1)',  text: '#DC2626' }
    :    s === 'warning'  ? { bg: 'rgba(245,158,11,0.1)', text: '#D97706' }
    :                       { bg: 'rgba(16,185,129,0.1)', text: '#10B981' };
};

const eventTypeColor = (raw: string | undefined) => {
  const t = norm(raw);
  return t === 'panic' || t === 'child-exit' ? { bg: 'rgba(239,68,68,0.1)',  text: '#DC2626' }
    :    t === 'incident' ? { bg: 'rgba(245,158,11,0.1)', text: '#D97706' }
    :    t === 'patrol'   ? { bg: 'rgba(109,40,217,0.1)', text: '#6D28D9' }
    :                       { bg: 'rgba(26,108,246,0.1)', text: '#1A6CF6' };
};

const SecurityPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isResident = user?.role === 'RESIDENT';
  const [items, setItems] = useState<SecurityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(INIT);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState('');

  const [confirmSos, setConfirmSos] = useState(false);
  const [sosSending, setSosSending] = useState(false);
  const [sosError, setSosError] = useState('');
  const [sosSent, setSosSent] = useState(false);

  const [childAlertSending, setChildAlertSending] = useState(false);
  const [childAlertMsg, setChildAlertMsg] = useState('');

  useEffect(() => {
    if (!societyId || isResident) { setLoading(false); return; }
    api.listLogs(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId, isResident]);

  const handleResolve = async (id: string) => {
    if (!societyId) return;
    setResolvingId(id); setResolveError('');
    try {
      const updated = await api.resolve(societyId, id);
      setItems(p => p.map(l => l.id === id ? updated : l));
    } catch (err) {
      setResolveError((err as ApiError).message || 'Failed to resolve incident.');
    } finally { setResolvingId(null); }
  };

  const handleRaiseSos = async () => {
    if (!societyId) return;
    setSosSending(true); setSosError('');
    try {
      await api.raiseSos(societyId);
      setConfirmSos(false);
      setSosSent(true);
    } catch (err) {
      setSosError((err as ApiError).message || 'Failed to raise SOS. Please try again or contact security directly.');
    } finally { setSosSending(false); }
  };

  const handleChildSafetyAlert = async () => {
    if (!societyId) return;
    const childName = window.prompt("Child's name (optional)?") || undefined;
    const location = window.prompt('Where was the child last seen (optional)?') || undefined;
    setChildAlertSending(true); setChildAlertMsg('');
    try {
      await api.raiseChildSafetyAlert(societyId, { childName, location });
      setChildAlertMsg('Security and admins have been notified.');
    } catch (err) {
      setChildAlertMsg((err as ApiError).message || 'Failed to send alert. Please contact security directly.');
    } finally { setChildAlertSending(false); }
  };

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError('');
    try {
      const created = await api.createLog(societyId, {
        eventType: form.eventType, severity: form.severity as SecurityLog['severity'],
        description: form.description || undefined, location: form.location || undefined,
      });
      setItems(p => [created, ...p]);
      setShowForm(false); setForm(INIT);
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to log security event');
    } finally { setSaving(false); }
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const criticalCount = items.filter(l => norm(l.severity) === 'critical').length;

  if (isResident) {
    return (
      <PageLayout>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Security
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Raise an emergency alert to notify society security and admins immediately.</p>
        </div>

        <div className="nx-card" style={{ padding: 40, textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
          {sosSent ? (
            <>
              <CheckCircle2 size={40} style={{ color: '#10B981', display: 'block', margin: '0 auto 16px' }} />
              <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 6 }}>SOS Sent</h3>
              <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Security and society admins have been notified. Help is on the way.</p>
              <button onClick={() => setSosSent(false)} className="nx-btn nx-btn-outline nx-btn-sm" style={{ marginTop: 16 }}>Done</button>
            </>
          ) : (
            <>
              <Siren size={40} style={{ color: '#EF4444', display: 'block', margin: '0 auto 16px' }} />
              <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 6 }}>Emergency SOS</h3>
              <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 20 }}>This immediately alerts your society's security and admin team.</p>
              <button
                onClick={() => setConfirmSos(true)}
                className="nx-btn nx-btn-primary"
                style={{ background: '#EF4444', borderColor: '#EF4444', gap: 8, padding: '10px 24px' }}
              >
                <Siren size={16} /> Raise Emergency SOS
              </button>
              <div style={{ marginTop: 16 }}>
                <button
                  onClick={handleChildSafetyAlert}
                  disabled={childAlertSending}
                  className="nx-btn nx-btn-outline"
                  style={{ gap: 8, padding: '10px 24px', opacity: childAlertSending ? 0.6 : 1 }}
                >
                  <Baby size={16} /> {childAlertSending ? 'Sending…' : 'Report Child Safety Alert'}
                </button>
                {childAlertMsg && <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 10 }}>{childAlertMsg}</p>}
              </div>
            </>
          )}
        </div>

        <ConfirmDialog
          open={confirmSos}
          variant="danger"
          title="Raise Emergency SOS?"
          message="This will immediately notify society security and admins. Only use this in a genuine emergency."
          confirmLabel={sosSending ? 'Sending…' : 'Raise SOS'}
          isLoading={sosSending}
          error={sosError}
          onConfirm={handleRaiseSos}
          onCancel={() => { setConfirmSos(false); setSosError(''); }}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Security
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
            {items.length} log entries
            {criticalCount > 0 && <span style={{ marginLeft: 8, color: '#DC2626', fontWeight: 600 }}>· {criticalCount} critical</span>}
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Log Event
        </button>
      </div>

      {resolveError && (
        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, fontSize: 12, color: '#EF4444', marginBottom: 16 }}>
          {resolveError}
        </div>
      )}

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            {
              key: 'eventType', header: 'Event Type',
              render: (l: SecurityLog) => {
                const normType = norm(l.eventType);
                const ec = eventTypeColor(normType);
                const label = EVENT_TYPES.find(t => t.value === normType)?.label || l.eventType || '—';
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {norm(l.severity) === 'critical' && <AlertTriangle size={12} style={{ color: '#DC2626', flexShrink: 0 }} />}
                    <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: ec.bg, color: ec.text }}>
                      {label}
                    </span>
                  </div>
                );
              },
            },
            {
              key: 'description', header: 'Description',
              cellStyle: { color: 'var(--nx-text-secondary)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              render: (l: SecurityLog) => l.description || '—',
            },
            {
              key: 'severity', header: 'Severity',
              render: (l: SecurityLog) => {
                const s = norm(l.severity);
                const sc = severityColor(s);
                const label = s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                    {label}
                  </span>
                );
              },
            },
            {
              key: 'location', header: 'Location',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (l: SecurityLog) => l.location || '—',
            },
            {
              key: 'loggedAt', header: 'Logged At',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (l: SecurityLog) => l.createdAt ? fmt(l.createdAt) : '—',
            },
            {
              key: 'status', header: 'Status',
              render: (l: SecurityLog) => {
                const resolved = l.status === 'RESOLVED';
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: resolved ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', color: resolved ? '#10B981' : '#D97706' }}>
                    {resolved ? 'Resolved' : 'Open'}
                  </span>
                );
              },
            },
            {
              key: 'actions', header: '',
              cellStyle: { textAlign: 'right', whiteSpace: 'nowrap' },
              render: (l: SecurityLog) => (
                l.status !== 'RESOLVED' && (
                  <button
                    onClick={() => handleResolve(l.id)}
                    disabled={resolvingId === l.id}
                    className="nx-btn nx-btn-sm"
                    style={{ fontSize: 10, padding: '3px 8px', background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 6, cursor: 'pointer', opacity: resolvingId === l.id ? 0.6 : 1 }}
                  >
                    {resolvingId === l.id ? 'Resolving…' : 'Resolve'}
                  </button>
                )
              ),
            },
          ]}
          rows={items}
          rowKey={l => l.id}
          loading={loading}
          emptyIcon={<Shield size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No security logs yet."
        />
      </div>

      {showForm && (
        <Modal
          title="Log Security Event" submitLabel="Log Event" error={formError} isLoading={saving}
          onClose={() => { setShowForm(false); setForm(INIT); setFormError(''); }}
          onSubmit={handleSubmit}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Event Type *</label>
              <select name="eventType" value={form.eventType} onChange={change} style={inp}>
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Severity *</label>
              <select name="severity" value={form.severity} onChange={change} style={inp}>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          <div>
            <label style={lbl}>Location</label>
            <input name="location" value={form.location} onChange={change} placeholder="Gate B, Parking Level 2…" style={inp} />
          </div>
          <div>
            <label style={lbl}>Description</label>
            <textarea
              name="description" value={form.description}
              onChange={change} placeholder="Describe the security event…"
              style={{ ...inp, minHeight: 80, resize: 'vertical' }}
            />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default SecurityPage;
